import { RadioButton, RadioButtonGroup, SkeletonText } from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { formatResource } from 'utils/misc';
import {
  createdSubtractDetailsLoadableAtom,
  participationFactorMutationAtom,
  refetchNriDataAtom,
  useParticipationFactorAtom,
} from './atoms';
import { PARTICIPATION_FACTOR_DROPDOWN_ITEMS } from './utils';
import styles from './participation-factor-dropdown.module.scss';

/**
 * Reads the persisted participation tract coverage threshold via atoms,
 * renders it as a RadioButtonGroup, and PATCHes on user change. Triggers an
 * NRI refetch on success.
 *
 * Display value is derived from the resource atom; an `override` covers the
 * window between user click and the next atom resolution (optimistic UI).
 *
 * @return {JSX.Element} The radio button group.
 */
export const ParticipationFactorDropdown = (): JSX.Element => {
  const [participationFactorMutationState, updateParticipationFactor] = useAtom(
    participationFactorMutationAtom
  );
  const subtractDetailsLoadable = useAtomValue(
    createdSubtractDetailsLoadableAtom
  );
  const subtractId =
    subtractDetailsLoadable.state === 'hasData'
      ? subtractDetailsLoadable.data?.id
      : undefined;
  const refetchNriReviewData = useSetAtom(refetchNriDataAtom);

  const participationFactorAtom = useParticipationFactorAtom(subtractId);
  const participationFactorResource = useAtomValue(participationFactorAtom);
  const { data: participationFactorData, loading: participationFactorLoading } =
    formatResource(participationFactorResource);

  const persistedFactor = participationFactorData?.participationTractFactor;
  const [override, setOverride] = useState<number | null>(null);
  const selectedValue = override ?? persistedFactor ?? null;

  const handleChange = useCallback(
    (value: number) => {
      if (!subtractId || value === selectedValue) return;

      setOverride(value);

      updateParticipationFactor({
        url: `/crm/subtracts/${subtractId}/subtract_intersection_analysis_parameters/null`,
        type: 'PATCH',
        data: {
          subtract_intersection_analysis_parameter: {
            participation_tract_factor: value,
          },
        },
        onSuccess: () => {
          refetchNriReviewData();
        },
        onError: (error: unknown) => {
          console.error('Failed to update participation factor:', error);
          setOverride(null);
        },
      });
    },
    [subtractId, selectedValue, updateParticipationFactor, refetchNriReviewData]
  );

  const isDisabled =
    participationFactorMutationState.loading ||
    participationFactorLoading ||
    !subtractId;

  return (
    <div className={styles.container}>
      <h6>Participation Tract Coverage</h6>
      <p className={styles.description}>
        Select a threshold to control which participation factors are rounded
        up. Lower thresholds round up more values closer to 100%, affecting the
        calculated NRI. Higher thresholds are more selective.
      </p>
      <div className={styles.thresholdControlContainer}>
        {participationFactorLoading ? (
          <SkeletonText className={styles.radioSkeleton} />
        ) : (
          <RadioButtonGroup
            name="participation-tract-coverage"
            valueSelected={selectedValue ?? undefined}
            disabled={isDisabled}
            onChange={(value) => {
              if (typeof value === 'number') handleChange(value);
            }}
            legendText="Participation Tract Coverage"
          >
            {PARTICIPATION_FACTOR_DROPDOWN_ITEMS.map((item) => (
              <RadioButton
                key={item.value}
                value={item.value}
                labelText={item.label}
              />
            ))}
          </RadioButtonGroup>
        )}
      </div>
    </div>
  );
};
